import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy} from '@angular/core';
import {EnergieEnum} from '../suivi-des-consommations/enums/EnergieEnum';
import {TypeReleveConsoEnum} from './enums/TypeReleveConsoEnum';
import {NgbDate, NgbDateParserFormatter} from '@ng-bootstrap/ng-bootstrap';
import {faCalendar, faCircleNotch} from '@fortawesome/free-solid-svg-icons';
import {NgbDateFrParserFormatterService} from '../../../services/ngb-date-fr-parser-formatter.service';
import {fadeInUpOnEnterAnimation} from '../../../animations';
import {EnergieService} from '../../../services/rt2012/energie.service';
import {AbstractControl, UntypedFormControl, UntypedFormGroup, ValidatorFn, Validators} from '@angular/forms';
import {Logement} from '../../../models/logement';
import {ConsommationsService} from '../../../services/rt2012/consommations.service';
import {Subscription} from 'rxjs';
import {ToastService} from '../../../services/toast.service';
import {ReleveIndividuel} from '../../../models/rt2012/ReleveIndividuel';
import {TypeProductionEnum} from '../../../models/rt2012/typeProductionEnum';
import {ErrorService} from "../../../services/error.service";

@Component({
    selector: 'app-edit-releve',
    templateUrl: './edit-releve.component.html',
    styleUrls: ['./edit-releve.component.scss'],
    providers: [
        { provide: NgbDateParserFormatter, useClass: NgbDateFrParserFormatterService }
    ],
    animations: [
        fadeInUpOnEnterAnimation()
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditReleveComponent implements OnInit, OnDestroy {
  EnergieEnum = EnergieEnum;
  TypeReleveConsoEnum = TypeReleveConsoEnum;
  faCalendar = faCalendar;
  faCircleNotch = faCircleNotch;

  @Input() public logement: Logement;
  @Input() public releve: ReleveIndividuel = null;
  @Input() public relevesIndividuels: ReleveIndividuel[] = [];
  @Output() public releveAdded = new EventEmitter();
  @Output() public releveUpdated = new EventEmitter();

  energiesAvailables: EnergieEnum[] = [];
  energiesEditables: EnergieEnum[] = [];
  lastConsommationsByEnergie: any = null;

  ajoutReleveForm: UntypedFormGroup;

  updating = false;
  submitting = false;

  private lastByEnergieSubscription: Subscription;
  private valeurChangeSubscription: Subscription;

  constructor(
    private energieService: EnergieService,
    private consommationsService: ConsommationsService,
    private toastService: ToastService,
    private errorService: ErrorService) {
  }

  ngOnInit(): void {
    this.initReleve();

    this.lastByEnergieSubscription = this.consommationsService.getLastByEnergie(this.logement.id)
      .subscribe(value => this.lastConsommationsByEnergie = value);

    let initDate = null;
    if (this.releve.date !== null) {
      initDate = new NgbDate(this.releve.date.getFullYear(), this.releve.date.getMonth() + 1, this.releve.date.getDate());
    }

    this.ajoutReleveForm = new UntypedFormGroup({
      energie: new UntypedFormControl(this.releve.energie, [
        Validators.required,
      ]),
      date: new UntypedFormControl(initDate, [
        Validators.required,
        this.correctDateValidator(),
        this.dateLibreValidator(this.relevesIndividuels, this.releve),
      ]),
      valeur: new UntypedFormControl(this.releve.valeur, [
        Validators.required,
        Validators.pattern(/^\d+(\.\d+)?$/),
        Validators.min(0)
      ]),
      typeReleve: new UntypedFormControl(this.releve.typeReleve, [
        Validators.required
      ]),
      chauffageMarche: new UntypedFormControl(this.releve.chauffageMarche, [
        Validators.required
      ]),
      refroidissementMarche: new UntypedFormControl(this.releve.refroidissementMarche, [
        Validators.required
      ]),
      nombreJourAbsence: new UntypedFormControl(this.releve.nombreJourAbsence, [
        Validators.required,
        Validators.pattern(/^\d+$/),
        Validators.min(0)
      ]),
    });
    this.energiesAvailables = this.energieService.getEnergiesAvailables(this.logement);
    this.energiesEditables = this.energieService.getEnergiesEditables(this.logement);

    this.valeurChangeSubscription = this.ajoutReleveForm.controls.valeur.valueChanges.subscribe(x => {
      const valeurControl = this.ajoutReleveForm.controls.valeur;
      const value = valeurControl.value;
      if (value.includes(',')) {
        valeurControl.setValue(value.replace(',', '.'));
      }
    });

  }

  private initReleve() {
    if (this.releve === null) {
      this.releve = new ReleveIndividuel();
      this.releve.logement = this.logement;
      this.releve.energie = null;
      this.releve.date = null;
      this.releve.valeur = null;
      this.releve.typeReleve = TypeReleveConsoEnum.TYPE_RELEVE_COMPTEUR;
      this.releve.chauffageMarche = false;
      this.releve.refroidissementMarche = false;
      this.releve.nombreJourAbsence = 0;
    } else {
      this.logement = this.releve.logement;
      this.updating = true;
    }
  }

  ngOnDestroy() {
    this.lastByEnergieSubscription?.unsubscribe();
    this.valeurChangeSubscription?.unsubscribe();
  }

  getUniteEnergie(): string {
    return this.energieService.getUnite(this.ajoutReleveForm.controls.energie.value);
  }

  correctDateValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if(control.value === null || typeof control.value === 'string'){
        return {
          correctDate: {
            value: control.value,
            reason: 'Le format de la date doit être sous la forme jj/mm/aaaa.'
          }
        };
      } else{
        return null;
      }
    };
  }

  dateLibreValidator(relevesIndividuels: ReleveIndividuel[], currentReleve: ReleveIndividuel): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (control.value === null || typeof control.value === 'string'){
        return null;
      }
      const date = new Date(control.value.year, control.value.month - 1, control.value.day, 0, 0, 0);
      const selectedEnergie = control.parent?.get('energie').value;
      if (relevesIndividuels
        .filter(r => r.energie === selectedEnergie)
        .filter(r => r.date !== currentReleve.date)// dans le cas de l'édition, exclusion de la date courante
        .map(r => r.date.toISOString())
        .includes(date.toISOString())) {
        return {
          dateLibre: {
            reason: 'Un autre relevé existe déjà à cette date pour l\'énergie séléctionnée.',
            value: control.value
          }
        };
      } else {
        return null;
      }
    };
  }

  selectedEnergieHasPreviousReleve(): boolean {
    return this.lastConsommationsByEnergie !== null &&
      this.lastConsommationsByEnergie[this.ajoutReleveForm.controls.energie.value] !== null;
  }

  onSubmit() {
    if (this.ajoutReleveForm.valid) {
      this.submitting = true;
      // En modification, this.releve est l'objet affiché dans l'historique : on garde son état pour le restaurer si le back refuse.
      const releveAvantModification = this.updating ? Object.assign(new ReleveIndividuel(), this.releve) : null;
      const formDate = this.ajoutReleveForm.controls.date.value as NgbDate;
      this.releve.date = new Date(formDate.year, formDate.month - 1, formDate.day, 0, 0, 0);
      this.releve.energie = this.ajoutReleveForm.controls.energie.value;
      this.releve.typeReleve = this.ajoutReleveForm.controls.typeReleve.value;
      this.releve.valeur = parseFloat(this.ajoutReleveForm.controls.valeur.value);
      this.releve.chauffageMarche = this.ajoutReleveForm.controls.chauffageMarche.value;
      this.releve.refroidissementMarche = this.ajoutReleveForm.controls.refroidissementMarche.value;
      this.releve.nombreJourAbsence = this.ajoutReleveForm.controls.nombreJourAbsence.value;

      if (this.updating) {
        this.consommationsService.update(this.releve).then((data: any) => {
          if (data instanceof ReleveIndividuel){
            this.releveUpdated.emit(data);
            this.toastService.showSuccess('Votre relevé a bien été modifié.');
          }else{
            Object.assign(this.releve, releveAvantModification);
            const errors = data.errors;
            this.errorService.toastErrors(errors);
          }
        }).catch(() => {
          Object.assign(this.releve, releveAvantModification);
          this.toastService.showError('Une erreur est survenue lors de la modification de votre relevé');
        }).finally(() => {
          this.submitting = false;
        });
      } else {
        this.consommationsService.create(this.releve).then((data: any) => {
          if (data instanceof ReleveIndividuel){
            this.releveAdded.emit(data);
            this.toastService.showSuccess('Votre relevé a bien été ajouté.');
          }else{
            const errors = data.errors;
            this.errorService.toastErrors(errors);
          }
        }).catch((e) => {
          this.toastService.showError('Une erreur est survenue lors de l\'ajout de votre relevé');
        }).finally(() => {
          this.submitting = false;
        });
      }
    }
  }

  chauffageEnMarcheIsDisplayed(): boolean {
    const selectedEnergie = this.ajoutReleveForm.controls.energie.value as EnergieEnum;
    switch (selectedEnergie) {
      case EnergieEnum.ELECTRICITE:
        return this.energieService.hasChauffageElec(this.logement.parametrageBatiment);
      case EnergieEnum.GAZ:
        return this.energieService.hasChauffageGaz(this.logement.parametrageBatiment);
    }
    return false;
  }

  refroidissementEnMarcheIsDisplayed(): boolean {
    return this.logement.parametrageBatiment.typeProductionRefroidissement === TypeProductionEnum.INDIVIDUEL &&
      this.ajoutReleveForm.controls.energie.value as EnergieEnum === EnergieEnum.ELECTRICITE;
  }

  hasRightColumn(): boolean {
    return this.refroidissementEnMarcheIsDisplayed() || this.chauffageEnMarcheIsDisplayed() || this.selectedEnergieHasPreviousReleve();
  }
}
